"use client";

/**
 * AuthContext.tsx — Authentication & Role Context
 *
 * 3-tier role system:
 *   super_admin   → LeadiumX staff. Sees all companies, can do everything.
 *   account_admin → Client admin. Can manage users in their company + see billing.
 *   user          → Regular user. No billing tab, no user management.
 *
 * Legacy "admin" role is treated as "account_admin" throughout.
 *
 * Exposed booleans for easy use in components:
 *   isSuperAdmin   → role === "super_admin"
 *   isAccountAdmin → role === "account_admin" (or legacy "admin")
 *   isAnyAdmin     → isSuperAdmin || isAccountAdmin  (replaces old isAdmin)
 *   canManageUsers → isAnyAdmin
 *   canViewBilling → isAccountAdmin || isSuperAdmin
 */

import type React from "react";
import { createContext, useContext, useEffect, useState, useMemo } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase, isSupabaseConfigured } from "../supabaseClient";

export type UserRole = "super_admin" | "account_admin" | "user";

interface UserProfile {
  company_name: string;
  role: UserRole;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profileLoading: boolean;

  // Auth actions
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  createUser: (userData: {
    firstName: string;
    lastName: string;
    companyName: string;
    email: string;
    role: UserRole;
  }) => Promise<{ error: any }>;
  updateUserRole: (userId: string, role: UserRole) => Promise<{ error: any }>;
  removeUser: (userId: string) => Promise<{ error: any }>;
  acceptInvite: (email: string, password: string, token: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  verifyOtp: (email: string, token: string, type: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;

  // Role flags
  isSuperAdmin: boolean;      // LeadiumX staff
  isAccountAdmin: boolean;    // Client admin
  isAnyAdmin: boolean;        // Either admin tier (replaces old isAdmin)
  isAdmin: boolean;           // Alias for isAnyAdmin (backwards compat)
  inviteUser: (email: string, companyName: string) => Promise<{ error: any }>; // Alias for createUser (backwards compat)
  canManageUsers: boolean;    // Can invite/edit/remove users
  canViewBilling: boolean;    // Can see the billing tab

  // Multi-tenant (super_admin only)
  switchToClient: (clientId: string) => void;
  currentClientId: string | null;
  clients: Array<{ id: string; name: string; email: string; company_name: string }>;
  refreshCompanies: () => Promise<void>;

  // Profile
  userProfile: UserProfile | null;

  // Credits / billing
  credits: number | null;
  creditsLoading: boolean;
  refreshCredits: () => Promise<void>;
  billingMode: "credits" | "dollars";
  isBalanceNegative: boolean;

  // Impersonation (super_admin only)
  impersonating: { userId: string; name: string; email: string; company: string } | null;
  startImpersonation: (target: { userId: string; name: string; email: string; company: string }) => boolean;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

/** Normalise legacy "admin" → "account_admin". */
function normaliseRole(raw?: string): UserRole {
  if (raw === "super_admin") return "super_admin";
  if (raw === "account_admin" || raw === "admin") return "account_admin";
  return "user";
}

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:8000";

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [currentClientId, setCurrentClientId] = useState<string | null>(() =>
    typeof window !== "undefined" ? localStorage.getItem("selectedClientId") : null
  );
  const [clients, setClients] = useState<
    Array<{ id: string; name: string; email: string; company_name: string }>
  >([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [billingMode, setBillingMode] = useState<"credits" | "dollars">("dollars");

  // ── Impersonation state (super_admin only) ───────────────────────────────
  const [impersonating, setImpersonating] = useState<{
    userId: string; name: string; email: string; company: string;
  } | null>(null);

  const startImpersonation = (target: { userId: string; name: string; email: string; company: string }): boolean => {
    // Switch the client context to the impersonated user's company
    const match = clients.find((c) => c.company_name === target.company);
    if (!match) {
      // Company not found in clients list — don't enter impersonation state
      return false;
    }
    setImpersonating(target);
    setCurrentClientId(match.id);
    localStorage.setItem("selectedClientId", match.id);
    localStorage.setItem("selectedCompanyName", match.company_name); // needed by agentService interceptors
    return true;
  };

  const stopImpersonation = () => {
    setImpersonating(null);
    localStorage.removeItem("selectedCompanyName");
    // Restore super_admin's own company context
    if (userProfile?.company_name) {
      const match = clients.find((c) => c.company_name === userProfile.company_name);
      if (match) {
        setCurrentClientId(match.id);
        localStorage.setItem("selectedClientId", match.id);
        return;
      }
    }
    // Fallback: super_admin has no company of their own — clear the client context
    setCurrentClientId(null);
    localStorage.removeItem("selectedClientId");
  };

  // ── Derived role flags ───────────────────────────────────────────────────
  const role = userProfile?.role ?? "user";
  // super_admin: either role in DB OR email ends with @leadiumx.ai (belt-and-suspenders)
  const isSuperAdmin   = role === "super_admin"; // SECURITY: role from DB only, never by email domain
  const isAccountAdmin = !isSuperAdmin && role === "account_admin";
  const isAnyAdmin     = isSuperAdmin || isAccountAdmin;
  const canManageUsers = isAnyAdmin;
  const canViewBilling = isAnyAdmin;         // account_admin + super_admin only

  const isBalanceNegative =
    credits !== null && billingMode === "dollars" && credits <= 0;

  const memoizedUserProfile = useMemo(() => userProfile, [userProfile]);

  // ── Data fetchers ────────────────────────────────────────────────────────

  const fetchCompanies = async () => {
    if (!isSupabaseConfigured) return;
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.user) return;
      const res = await fetch(`${SERVER_URL}/api/companies`, {
        headers: { 'user-id': s.user.id },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      if (!result.error) setClients(result.companies);
    } catch (err) {
      console.log("[auth] fetchCompanies error:", err);
    }
  };

  const fetchUserProfile = async (userId: string) => {
    if (!isSupabaseConfigured) return;
    setProfileLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/user-profile/${userId}`, {
        headers: { "user-id": userId },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      if (result.error) return;

      const profile: UserProfile = {
        ...result.profile,
        role: normaliseRole(result.profile?.role),
      };
      setUserProfile(profile);

      // Non-super-admins are pinned to their OWN company and must never carry a
      // selected-company override. A stale "selectedCompanyName" in localStorage
      // (e.g. left over from a prior session) is sent by the agent-service
      // interceptors as x-company-name and surfaces another tenant's data, so we
      // clear it here for anyone who is not a super_admin.
      const profileIsSuperAdmin = profile.role === "super_admin";
      if (!profileIsSuperAdmin) {
        localStorage.removeItem("selectedCompanyName");
        localStorage.removeItem("selectedClientId");
        setCurrentClientId(null);
      }
    } catch (err) {
      console.log("[auth] fetchUserProfile error:", err);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchCredits = async () => {
    if (!isSupabaseConfigured) return;
    setCreditsLoading(true);
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.user) return; // finally still runs

      const headers: Record<string, string> = { "user-id": s.user.id };

      // Only send company header for admin users switching context
      // Backend now verifies role from DB — no longer trusts x-is-admin
      if (isAnyAdmin && currentClientId) {
        const selected = clients.find((c) => c.id === currentClientId);
        if (selected) {
          headers["x-company-name"] = selected.company_name;
        }
      }

      const res = await fetch(`${SERVER_URL}/api/billing/subscription`, { headers });

      // Non-admin users get 403 — that's expected, just skip setting credits
      if (res.status === 403) return;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const result = await res.json();
      if (result.credits) {
        setCredits(result.credits.total_credits - result.credits.used_credits);
      }
      if (result.billingMode) setBillingMode(result.billingMode);
    } catch (err) {
      console.log("[auth] fetchCredits error:", err);
    } finally {
      setCreditsLoading(false);
    }
  };

  // ── Auth actions ─────────────────────────────────────────────────────────

  const signIn = async (email: string, password: string) => {
    if (!isSupabaseConfigured) return { error: { message: "Supabase not configured" } };
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error ?? null };
    } catch (err) {
      return { error: err };
    }
  };

  const signOut = async () => {
    if (isSupabaseConfigured) await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setCurrentClientId(null);
    setCredits(null);
    setCreditsLoading(false);
    setBillingMode("dollars");
    setUserProfile(null);
    // Clear persisted impersonation/company state so next login starts clean
    if (typeof window !== "undefined") {
      localStorage.removeItem("selectedClientId");
      localStorage.removeItem("selectedCompanyName");
    }
  };

  const createUser = async (userData: {
    firstName: string;
    lastName: string;
    companyName: string;
    email: string;
    role: UserRole;
  }) => {
    if (!isSupabaseConfigured) return { error: { message: "Supabase not configured" } };
    try {
      const callerSession = await supabase.auth.getSession();
      const callerId = callerSession.data.session?.user?.id;

      const res = await fetch(`${SERVER_URL}/api/create-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(callerId ? { "user-id": callerId } : {}),
        },
        body: JSON.stringify({
          firstName: userData.firstName,
          lastName: userData.lastName,
          companyName: userData.companyName,
          email: userData.email,
          role: userData.role,
        }),
      });

      const result = await res.json();
      if (!res.ok) return { error: { message: result.details || result.error || "Failed to create user" } };
      await fetchCompanies();
      return { error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create user";
      return { error: { message: msg } };
    }
  };

  const updateUserRole = async (userId: string, newRole: UserRole) => {
    if (!isSupabaseConfigured) return { error: { message: "Supabase not configured" } };
    try {
      const callerSession = await supabase.auth.getSession();
      const callerId = callerSession.data.session?.user?.id;

      const res = await fetch(`${SERVER_URL}/api/users/${userId}/role`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(callerId ? { "user-id": callerId } : {}),
        },
        body: JSON.stringify({ role: newRole }),
      });

      const result = await res.json();
      if (!res.ok) return { error: { message: result.error || "Failed to update role" } };
      return { error: null };
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : "Failed to update role" } };
    }
  };

  const removeUser = async (userId: string) => {
    if (!isSupabaseConfigured) return { error: { message: "Supabase not configured" } };
    try {
      const callerSession = await supabase.auth.getSession();
      const callerId = callerSession.data.session?.user?.id;

      const res = await fetch(`${SERVER_URL}/api/users/${userId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          ...(callerId ? { "user-id": callerId } : {}),
        },
      });

      const result = await res.json();
      if (!res.ok) return { error: { message: result.error || "Failed to remove user" } };
      return { error: null };
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : "Failed to remove user" } };
    }
  };

  const acceptInvite = async (email: string, password: string, token: string) => {
    if (!token || token.length < 10) return { error: { message: "Invalid invitation token" } };
    if (!isSupabaseConfigured) return { error: { message: "Supabase not configured" } };
    try {
      // Use Supabase's native verifyOtp for invite tokens — no custom server endpoint needed
      const { error: otpError } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "invite",
      });
      if (otpError) return { error: otpError };
      // Set the password now that the session is active
      const { error: pwError } = await supabase.auth.updateUser({ password });
      if (pwError) return { error: pwError };
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const resetPassword = async (email: string) => {
    if (!isSupabaseConfigured) return { error: { message: "Supabase not configured" } };
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/set-new-password`,
      });
      return { error: error ?? null };
    } catch (err) {
      return { error: err };
    }
  };

  const verifyOtp = async (email: string, token: string, type: string) => {
    if (!isSupabaseConfigured) return { error: { message: "Supabase not configured" } };
    try {
      const { error } = await supabase.auth.verifyOtp({ email, token, type: type as any });
      return { error: error ?? null };
    } catch (err) {
      return { error: err };
    }
  };

  const updatePassword = async (password: string) => {
    if (!isSupabaseConfigured) return { error: { message: "Supabase not configured" } };
    try {
      const { error } = await supabase.auth.updateUser({ password });
      return { error: error ?? null };
    } catch (err) {
      return { error: err };
    }
  };

  const switchToClient = (clientId: string) => {
    if (impersonating) stopImpersonation();
    setCurrentClientId(clientId);
    if (typeof window !== "undefined") {
      localStorage.setItem("selectedClientId", clientId);
      const selected = clients.find((c) => c.id === clientId);
      if (selected) localStorage.setItem("selectedCompanyName", selected.company_name);
    }
  };

  const refreshCompanies = async () => fetchCompanies();
  const refreshCredits   = async () => fetchCredits();

  // ── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
      if (s?.user) fetchCompanies().then(() => { fetchUserProfile(s.user.id).then(() => { fetchCredits(); }); });
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
      if (s?.user) {
        fetchCompanies().then(() => { fetchUserProfile(s.user.id).then(() => { fetchCredits(); }); });
      } else {
        setClients([]); setUserProfile(null); setCurrentClientId(null);
        setCredits(null); setCreditsLoading(false); setBillingMode("dollars");
        setProfileLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto-select company when clients load for non-super admins
  useEffect(() => {
    if (isAnyAdmin && !isSuperAdmin && userProfile?.company_name && clients.length > 0 && !currentClientId) {
      const match = clients.find((c) => c.company_name === userProfile.company_name);
      if (match) { setCurrentClientId(match.id); localStorage.setItem("selectedClientId", match.id); }
    }
  }, [clients, userProfile, isAnyAdmin, isSuperAdmin, currentClientId]);

  useEffect(() => { if (currentClientId) fetchCredits(); }, [currentClientId, isAnyAdmin]);

  useEffect(() => {
    const handle = () => { if (document.visibilityState === "visible" && user && currentClientId) fetchCredits(); };
    document.addEventListener("visibilitychange", handle);
    return () => document.removeEventListener("visibilitychange", handle);
  }, [user, currentClientId]);

  // ── Context value ────────────────────────────────────────────────────────

  const value: AuthContextType = {
    user, session, loading,
    signIn, signOut, createUser, updateUserRole, removeUser,
    acceptInvite, resetPassword, verifyOtp, updatePassword,
    isSuperAdmin, isAccountAdmin, isAnyAdmin, isAdmin: isAnyAdmin, canManageUsers, canViewBilling,
    switchToClient, currentClientId, clients, refreshCompanies,
    userProfile: memoizedUserProfile,
    profileLoading,
    credits, creditsLoading, refreshCredits, billingMode, isBalanceNegative,
    inviteUser: (email: string, companyName: string) => createUser({ email, companyName, firstName: '', lastName: email.split('@')[0], role: 'user' as UserRole }),
    impersonating, startImpersonation, stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
